"use client";

import React from "react";
import { ArrowRight, BookOpen, CircleCheck as CheckCircle2, Download, ExternalLink, FileCheck2, FileCode2, FileSpreadsheet, FileText, Flame, CircleQuestionMark as HelpCircle, Layers, ShieldAlert, Sparkles } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
interface ResourceItem {
  id: string;
  title: string;
  category: string;
  docType: string;
  size: string;
  summary: string;
  standard: string;
  linkPath: string;
}
const resourcesList: ResourceItem[] = [{
  id: "res-1",
  title: "ATEX & IECEx Zone 0/1/2 PAT Spectrometer Integration Guide",
  category: "Compliance Guide",
  docType: "PDF Guide",
  size: "3.4 MB",
  summary: "Comprehensive engineering guide on mechanical mounting, optical feedthroughs, and purge validation for NIR spectrometers in explosive gas atmospheres.",
  standard: "ATEX 2014/34/EU • IECEx 60079-0",
  linkPath: "/MicroNirPatWxPage"
}, {
  id: "res-2",
  title: "Continuous Fluid Bed Drying Endpoint Monitoring in Class I Div 1 Zones",
  category: "Application Note",
  docType: "App Note AN-402",
  size: "2.1 MB",
  summary: "Real-time moisture monitoring and endpoint determination in fluidised bed granulators using intrinsically safe fiber-optic probes.",
  standard: "Class I, Div 1 • Groups C & D",
  linkPath: "/DryingEndpointPage"
}, {
  id: "res-3",
  title: "Solvent Recovery & HPLC Waste Stream Analysis in Hazardous Plant Areas",
  category: "Technical Paper",
  docType: "Tech Whitepaper",
  size: "4.8 MB",
  summary: "Safety verification and analytical precision standards for closed-loop solvent recovery systems deployed in regulated chemical environments.",
  standard: "Zone 1 Ex d [ia] IIC T4",
  linkPath: "/HplcSolventRecoveryResolvePage"
}, {
  id: "res-4",
  title: "Rapid Chemical & Explosives Identification for First Responders",
  category: "Field Protocol",
  docType: "Quick Reference",
  size: "1.9 MB",
  summary: "Operational procedures and safety thresholds for 1064nm handheld Raman identification through colored glass and opaque polymer containers.",
  standard: "MIL-STD-810G • IP68",
  linkPath: "/QuickDetectTraceExplosivesPage"
}, {
  id: "res-5",
  title: "Purge & Pressurization (Ex p) Best Practices for Process Enclosures",
  category: "Engineering Note",
  docType: "Tech Note EN-118",
  size: "2.6 MB",
  summary: "Control flowrates, minimum purge cycles, and fail-safe interlocking protocols for installing non-certified analytical instruments into Ex zones.",
  standard: "EN/IEC 60079-2",
  linkPath: "/PatInstallationIntegrationPage"
}, {
  id: "res-6",
  title: "Chemometric Model Transfer & Validation for Hazardous Area Analyzers",
  category: "Validation Standard",
  docType: "Method Guide",
  size: "3.1 MB",
  summary: "Protocol templates for transferring calibration models between laboratory benchtop spectrometers and ATEX-certified field analyzers.",
  standard: "ICH Q2(R1) • USP <1058>",
  linkPath: "/ChemometricModelDevelopmentPage"
}];
const certificationStandards = [{
  name: "ATEX Directive 2014/34/EU",
  desc: "European explosion protection compliance for equipment used in flammable gas or dust atmospheres."
}, {
  name: "IECEx International Scheme",
  desc: "Global certification verifying intrinsic safety (Ex i), flameproof (Ex d), and purged systems (Ex p)."
}, {
  name: "NEC / NFPA 70 Standards",
  desc: "North American hazardous location definitions covering Class I/II, Division 1/2 operations."
}, {
  name: "GAMP 5 & 21 CFR Part 11",
  desc: "Data integrity, audit trails, and computerized analytical system lifecycle compliance."
}];
export default function HazardousResources() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="hazardousarearesources" data-nav="light" className="bg-background text-foreground py-16 md:py-24 border-b border-border">
      <div className="page-container flex flex-col gap-12 md:gap-16">
        
        {/* Section Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 border-b border-border pb-8">
          <div className="max-w-2xl flex flex-col gap-3">
            <div className="flex items-center gap-2">
              <Badge variant="outline" className="text-primary border-primary/30 px-2.5 py-1 text-xs font-semibold uppercase tracking-wider">
                <Flame className="size-3.5 mr-1" />
                Technical Documentation
              </Badge>
              <span className="text-xs text-muted-foreground">ATEX • IECEx • Class I Div 1/2</span>
            </div>
            
            <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-foreground font-default">
              Hazardous Area Technical Resources
            </h2>
            
            <p className="text-base md:text-lg text-muted-foreground leading-relaxed">
              Access application notes, qualification guides, engineering drawings, and regulatory references for installing analytical systems in explosive and regulated environments.
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-3">
            <Button asChild variant="outline">
              <Link to="/ResourcesPage">
                <BookOpen className="size-4 mr-2" />
                All Resources
              </Link>
            </Button>
            <Button asChild variant="default">
              <Link to="/DiscussYourApplicationPage">
                Speak with an ATEX Engineer
                <ArrowRight className="size-4 ml-2" />
              </Link>
            </Button>
          </div>
        </div>

        {/* Resources Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {resourcesList.map((resource, i) => <motion.div key={resource.id} data-index={i} initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: "-50px"
        }} transition={{
          duration: 0.35,
          delay: shouldReduceMotion ? 0 : i * 0.08
        }} className="h-full flex">
              <Card className="flex flex-col justify-between w-full h-full border border-border bg-card hover:border-primary/50 transition-colors shadow-xs">
                <CardHeader className="gap-2.5 pb-4">
                  <div className="flex items-center justify-between gap-2">
                    <Badge variant="secondary" className="font-medium text-xs">
                      {resource.category}
                    </Badge>
                    <span className="text-xs text-muted-foreground font-mono">
                      {resource.size}
                    </span>
                  </div>
                  <CardTitle className="text-lg font-semibold leading-snug line-clamp-2 text-card-foreground">
                    {resource.title}
                  </CardTitle>
                  <div className="flex items-center gap-1.5 text-xs text-primary font-medium">
                    <ShieldAlert className="size-3.5" />
                    <span>{resource.standard}</span>
                  </div>
                </CardHeader>

                <CardContent className="pb-4">
                  <CardDescription className="text-sm text-muted-foreground line-clamp-3 leading-relaxed">
                    {resource.summary}
                  </CardDescription>
                </CardContent>

                <CardFooter className="pt-2 border-t border-border flex items-center justify-between mt-auto">
                  <span className="text-xs text-muted-foreground flex items-center gap-1">
                    <FileText className="size-3.5" />
                    {resource.docType}
                  </span>
                  
                  <Button asChild size="sm" variant="ghost" className="text-primary hover:text-primary hover:bg-primary/10">
                    <Link to={resource.linkPath}>
                      <span>Review Details</span>
                      <ArrowRight className="size-3.5 ml-1" />
                    </Link>
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>)}
        </div>

        {/* Reference Framework & Direct Application Help Banner */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 pt-4">
          
          {/* Certification Summary */}
          <div className="lg:col-span-2 rounded-xl border border-border bg-secondary/40 p-6 md:p-8 flex flex-col gap-6">
            <div className="flex flex-col gap-1.5">
              <div className="flex items-center gap-2 text-foreground font-semibold text-lg">
                <FileCheck2 className="size-5 text-primary" />
                <span>Applicable Standards & Compliance Frameworks</span>
              </div>
              <p className="text-sm text-muted-foreground">
                All hazardous area analytical systems deployed by Antech Solutions are verified against international explosion safety and data integrity directives.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {certificationStandards.map((item, index) => <div key={item.name} data-index={index} className="flex flex-col gap-1 rounded-lg bg-background p-3.5 border border-border/80">
                  <div className="flex items-center gap-2 font-medium text-sm text-foreground">
                    <CheckCircle2 className="size-4 text-primary shrink-0" />
                    <span>{item.name}</span>
                  </div>
                  <p className="text-xs text-muted-foreground pl-6 leading-relaxed">
                    {item.desc}
                  </p>
                </div>)}
            </div>
          </div>

          {/* Direct Consultation Callout */}
          <div className="rounded-xl border border-border bg-card p-6 md:p-8 flex flex-col justify-between gap-6 shadow-xs">
            <div className="flex flex-col gap-3">
              <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
                <HelpCircle className="size-5" />
              </div>
              <h3 className="text-lg font-bold text-card-foreground">
                Need a Custom Feasibility Review?
              </h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Our application specialists examine process conditions, zoning boundaries, sample optics, and integration requirements for your exact plant area.
              </p>
            </div>

            <div className="flex flex-col gap-2.5">
              <Button asChild variant="default" className="w-full">
                <Link to="/FeasibilityStudiesPage">
                  Request Feasibility Study
                </Link>
              </Button>
              <Button asChild variant="outline" className="w-full">
                <Link to="/RequestAQuotePage">
                  Request Hazardous Area Quote
                </Link>
              </Button>
            </div>
          </div>

        </div>

      </div>
    </section>;
}